import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { apiurl } from "../config/config";
import Layout, { Container } from "../components/layout";
import { getCookie } from "../config/webStorage";
import { useUser } from "../config/userProvider";
import dayjs from "dayjs";
import { AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import EmailInputField from "../components/emailInputField";

const Badge = ({ status }) => {
  const colors = {
    created: "bg-gray-100 text-gray-700",
    hired: "bg-green-100 text-green-700",
    handoverToTrainer: "bg-blue-100 text-blue-700",
    dropped: "bg-red-100 text-red-700",
    joined: "bg-purple-100 text-purple-700",
  };

  return (
    <span
      className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${
        colors[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
};

const ManageBatch = () => {
  const { batchObjId } = useParams();
  const { userData } = useUser();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const [selectedApps, setSelectedApps] = useState([]);

  const toggleSelectApp = (id) => {
    setSelectedApps((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedApps.length === batch.applications.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(batch.applications.map((a) => a.applicationObjId));
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [batchReportModalOpen, setBatchReportModalOpen] = useState(false);
  const statusFlow = {
    created: ["handoverToInduction"],
    handoverToInduction: ["receivedByInduction"],
    receivedByInduction: ["handoverToTrainer"],
    handoverToTrainer: ["receivedByTrainer"],
    receivedByTrainer: ["handoverToOJT"],
    handoverToOJT: ["receivedByOJT"],
    receivedByOJT: ["handoverToFloor"],
    handoverToFloor: ["receivedByFloor"],
    receivedByFloor: ["completed"],
    completed: ["closed"],
    closed: [], // final state
  };

  const getNextBatchStatuses = (current) => {
    return statusFlow[current] || [];
  };

  useEffect(() => {
    fetchBatchDetails();
    // eslint-disable-next-line
  }, []);

  const fetchBatchDetails = async () => {
    try {
      const token = getCookie("swtoken");
      const { data } = await axios.get(
        `${apiurl}/api/batch/get/${batchObjId}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );
      setBatch(data.batch);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch batch");
    } finally {
      setLoading(false);
    }
  };

  const updateBatchStatus = async (newStatus, remarks) => {
    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/batch/updateBatchStatus`,
        {
          userObjId: userData?._id,
          batchStatus: newStatus,
          batchObjId,
          remarks,
        },
        { headers: { Authorization: `${token}` } }
      );
      fetchBatchDetails();
    } catch (err) {
      console.error("Error updating batch status:", err);
      alert("Failed to update batch status");
    }
  };

  // ----------------- Bulk Attendance Section -----------------
  const [attendanceDate, setAttendanceDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // yyyy-mm-dd
  const [attendanceStatus, setAttendanceStatus] = useState("present");
  const [trainingStatus, setTrainingStatus] = useState("");

  const markBulkAttendance = async () => {
    if (!selectedApps.length) {
      alert("Please select at least one application");
      return;
    }

    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/batch/mark-bulk-attendance`,
        {
          userObjId: userData?._id,
          batchObjId: batchObjId,
          applicationObjIds: selectedApps,
          date: attendanceDate,
          status: attendanceStatus,
        },
        { headers: { Authorization: `${token}` } }
      );
      setSelectedApps([]); // clear selection
      fetchBatchDetails(); // refresh
    } catch (err) {
      console.error("Error marking attendance:", err);
      alert("Failed to mark attendance");
    }
  };

  const markAbscondedApplications = async () => {
    if (!selectedApps.length) {
      alert("Please select at least one application");
      return;
    }

    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/batch/mark-bulk-abscond`,
        {
          userObjId: userData?._id,
          batchObjId: batchObjId,
          applicationObjIds: selectedApps,
        },
        { headers: { Authorization: `${token}` } }
      );
      setSelectedApps([]); // clear selection
      fetchBatchDetails(); // refresh
    } catch (err) {
      console.error("Error marking attendance:", err);
      alert("Failed to mark attendance");
    }
  };

  const [stage, setStage] = useState("induction");

  const handleMarkStage = async () => {
    if (!selectedApps?.length) {
      toast.error("No applications selected!");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${apiurl}/api/batch/mark-bulk-stage`, {
        userObjId: userData?._id,
        batchObjId: batchObjId,
        applicationObjIds: selectedApps,
        stage,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        // 🔹 Optionally, update local state with updated batch
        // For example, if you have a batch state:
        // setBatch(response.data.batch);
      } else {
        toast.error(response.data.message || "Failed to mark stage");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Mark Joined
  const markBulkJoined = async () => {
    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/batch/mark-bulk-joined`,
        {
          batchObjId: batchObjId,
          applicationObjIds: selectedApps,
          userObjId: userData?._id, // TODO: use logged-in user
          remarks: "Marked as Joined",
        },
        { headers: { Authorization: `${token}` } }
      );
      setSelectedApps([]);
      fetchBatchDetails();
    } catch (err) {
      console.error("Mark Joined failed:", err);
      alert("Failed to mark joined");
    }
  };

  // ✅ Mark Training Started
  const markBulkTrainingStarted = async () => {
    if (!selectedApps.length) {
      return alert("Please select at least one application to start training");
    }
    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/application/training/start-training`,
        {
          applicationObjIds: selectedApps,
          userObjId: userData?._id, // logged-in user
        },
        { headers: { Authorization: `${token}` } }
      );

      setSelectedApps([]);
      fetchBatchDetails(); // refresh batch details after update
      alert("Training marked as started for selected applications");
    } catch (err) {
      console.error("Mark Training Started failed:", err);
      alert("Failed to mark training started");
    }
  };

  // ✅ Mark Training Completed
  const markBulkTrainingCompleted = async () => {
    if (!selectedApps.length) {
      return alert(
        "Please select at least one application to complete training"
      );
    }

    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/application/training/mark-complete`,
        {
          applicationObjIds: selectedApps,
          userObjId: userData?._id, // logged-in user
          feedback: "Training Completed", // optional feedback
        },
        { headers: { Authorization: `${token}` } }
      );

      setSelectedApps([]);
      fetchBatchDetails(); // refresh batch details after update
      alert("Training marked as completed for selected applications");
    } catch (err) {
      console.error("Mark Training Completed failed:", err);
      alert("Failed to mark training completed");
    }
  };

  // ✅ Mark Induction Completed
  const markBulkInductionCompleted = async () => {
    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/batch/mark-bulk-induction-completed`,
        {
          batchObjId: batchObjId,
          applicationObjIds: selectedApps,
          userObjId: userData?._id, // TODO: use logged-in user
          remarks: "Induction Completed",
        },
        { headers: { Authorization: `${token}` } }
      );
      setSelectedApps([]);
      fetchBatchDetails();
    } catch (err) {
      console.error("Mark Induction Completed failed:", err);
      alert("Failed to mark induction completed");
    }
  };

  const updateTrainingStatus = async () => {
    if (!selectedApps.length) {
      alert("Please select at least one application");
      return;
    }

    try {
      const token = getCookie("swtoken");

      await axios.post(
        `${apiurl}/api/application/bulk-update-training-status`,
        {
          applicationObjIds: selectedApps, // array of selected applications
          status: trainingStatus, // new training status (pending, completed, etc.)
          userObjId: userData?._id, // who updated
        },
        { headers: { Authorization: `${token}` } }
      );

      // reset state
      setSelectedApps([]);
      setTrainingStatus("");
      fetchBatchDetails(); // refresh data
    } catch (err) {
      console.error("Error updating training status:", err);
      alert("Failed to update training status");
    }
  };

  const getUniqueDates = (applications = []) => {
    const allDates = [];
    applications?.forEach((app) => {
      app?.attendance?.forEach((a) => {
        if (a?.date) {
          allDates.push(new Date(a.date).toISOString().split("T")[0]); // yyyy-mm-dd
        }
      });
    });
    return [...new Set(allDates)].sort(); // unique + sorted
  };

  const dates = getUniqueDates(batch?.applications || []);

  // State for popup
  const [viewApp, setViewApp] = useState(null);

  // Open modal
  const handleViewApplication = (app) => {
    setViewApp(app);
  };

  // Close modal and refresh
  const handleCloseApplication = () => {
    setViewApp(null);
    fetchBatchDetails(); // refresh after close
  };

  const [show, setShow] = useState(false);
  const [url, setUrl] = useState("");
  const [showAddAppModal, setShowAddAppModal] = useState(false);

  // Add application to batch
  const addApplicationToBatch = async (applicationObjIds) => {
    console.log("applicationObjIds", applicationObjIds);
    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/batch/add-application`,
        {
          batchObjId,
          addApplications: applicationObjIds,
          userObjId: userData?._id,
          remarks: "Added to batch manually",
        },
        { headers: { Authorization: `${token}` } }
      );
      fetchBatchDetails();
    } catch (err) {
      console.error("Error adding application:", err);
      alert("Failed to add application");
    }
  };

  //   // Remove application from batch
  //   const removeApplicationFromBatch = async (applicationObjId) => {
  //       if (!window.confirm("Are you sure you want to remove this application from the batch?")) {
  //     return; // ❌ stop if cancelled
  //   }
  //     try {
  //       const token = getCookie("swtoken");
  //       await axios.post(
  //         `${apiurl}/api/batch/remove-application`,
  //         {
  //           batchObjId,
  //           removeApplications: [applicationObjId],
  //           userObjId: userData?._id,
  //           remarks: "Removed from batch manually",
  //         },
  //         { headers: { Authorization: `${token}` } }
  //       );
  //       fetchBatchDetails();
  //     } catch (err) {
  //       console.error("Error removing application:", err);
  //       alert("Failed to remove application");
  //     }
  //   };
  // state for confirmation
  const [confirmData, setConfirmData] = useState(null);

  const handleRemoveClick = (applicationObjId) => {
    setConfirmData(applicationObjId); // open confirm modal
  };

  const confirmRemove = async () => {
    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/batch/remove-application`,
        {
          batchObjId,
          removeApplications: [confirmData],
          userObjId: userData?._id,
          remarks: "Removed from batch manually",
        },
        { headers: { Authorization: `${token}` } }
      );
      fetchBatchDetails();
      setConfirmData(null); // close modal
    } catch (err) {
      console.error("Error removing application:", err);
      alert("Failed to remove application");
    }
  };


    const markApplicationsBulkStatus = async (status) => {
    if (!selectedApps.length) {
      alert("Please select at least one application");
      return;
    }

    try {
      const token = getCookie("swtoken");
      await axios.post(
        `${apiurl}/api/batch/mark-application-bulk-status`,
        {
          userObjId: userData?._id,
          batchObjId: batchObjId,
          applicationObjIds: selectedApps,
          "applicationStatus": status,
          remarks: `Marked as ${status}`,
        },
        { headers: { Authorization: `${token}` } }
      );
      setSelectedApps([]); // clear selection
      fetchBatchDetails(); // refresh
    } catch (err) {
      console.error("Error marking attendance:", err);
      alert("Failed to mark attendance");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-lg font-medium text-gray-600 animate-pulse">
          Loading batch details...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-600 font-semibold">{error}</p>
      </div>
    );
  }

  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="min-h-screen bg-gray-50 p-2 space-y-6">
          {/* Batch Header */}
          <div className="bg-white shadow-sm rounded-2xl p-6 border">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-800">
                {batch.batchName} ({batch.id}){" "}
                <Badge status={batch.batchStatus} />
              </h1>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    //   setNextOptions(getNextBatchStatuses(batch.batchStatus));
                    setModalOpen(true);
                  }}
                  className="bg-blue-600 text-white px-4 py-1 rounded-lg"
                >
                  Move to Next Stage
                </button>
                <button
                  onClick={() => setShowConfirm(true)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-sm text-white font-medium rounded-md shadow-md"
                >
                  Mark Draft
                </button>
                <button
                  onClick={() => setBatchReportModalOpen(true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-sm text-white font-medium rounded-md shadow-md"
                >
                  Send Batch Report
                </button>
                <SendBatchReportModal
                  show={batchReportModalOpen}
                  onClose={() => setBatchReportModalOpen(false)}
                  // onSend={handleSendReport}
                  // startDate={batch.startDate}
                  // endDate={batch.endDate}
                  reportData={batch.applications}
                />
              </div>
            </div>
            <p className="text-gray-600 mt-2">{batch.description}</p>
            <div className="flex justify-between mt-5">
              <div className="flex flex-wrap gap-6 text-sm items-center">
                <div>
                  <span className="text-gray-500">Batch Date:</span>
                  <p className="font-medium">
                    {new Date(batch.batchDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="font-medium">
                    {new Date(batch.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="">
                  <h2 className=" font-semibold text-gray-800">Created By</h2>
                  <p className="text-gray-700">
                    {batch.createdBy?.userName} ({batch.createdBy?.userEmail})
                  </p>
                </div>
              </div>
              <div className="">
                <p className="text-6xl">
                  {batch?.reports?.totalApplications || "0"}
                </p>
              </div>
            </div>
          </div>

          {showConfirm && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
              <div className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-xl animate-fadeIn">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                  <h2 className="text-lg font-semibold text-gray-800">
                    Confirm Update
                  </h2>
                </div>

                <p className="text-gray-600 mb-6">
                  Are you sure you want to update this batch status to{" "}
                  <span className="font-bold text-blue-600">Draft</span>?
                </p>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowConfirm(false)}
                    disabled={loading}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => (
                      updateBatchStatus("draft", "Moved to draft"),
                      setShowConfirm(false)
                    )}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                  >
                    {loading ? "Updating..." : "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <StatusUpdateModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            currentStatus={batch.batchStatus}
            options={getNextBatchStatuses(batch.batchStatus)}
            onUpdate={async (newStatus, remarks) => {
              await updateBatchStatus(newStatus, remarks);
              setModalOpen(false);
            }}
          />

          {/* Reports */}
          <div className="bg-white shadow-md rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">
              Reports
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(batch?.reports.statusBreakdown).map(
                ([key, value]) => (
                  <div
                    key={key}
                    className="bg-gray-50 border rounded-xl p-4 shadow hover:shadow-md transition"
                  >
                    <p className="text-sm text-gray-500 capitalize">{key}</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {value.count}
                    </p>
                  </div>
                )
              )}
            </div>
          </div>

          {confirmData && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Confirm Removal
                </h2>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to remove this application from the
                  batch?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setConfirmData(null)}
                    className="px-4 py-2 rounded-lg border"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRemove}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white shadow-xl rounded-2xl p-6">
            {/* Bulk Attendance Section */}
            {selectedApps.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-5 rounded-xl mb-6 flex flex-col md:flex-row items-center gap-4 shadow-inner border border-green-200">
                <p className="font-semibold text-gray-700 text-lg">
                  Mark Attendance for{" "}
                  <span className="text-green-600">{selectedApps.length}</span>{" "}
                  selected
                </p>

                <div className="flex gap-3 items-center flex-wrap">
                  <label className="text-gray-600 font-medium">Date:</label>
                  <input
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    className="border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-green-400 focus:outline-none transition"
                  />
                </div>

                <div className="flex gap-3 items-center flex-wrap">
                  <label className="text-gray-600 font-medium">Status:</label>
                  <select
                    value={attendanceStatus}
                    onChange={(e) => setAttendanceStatus(e.target.value)}
                    className="border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-green-400 focus:outline-none transition"
                  >
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="weekoff">Week Off</option>
                  </select>
                </div>

                <button
                  onClick={markBulkAttendance}
                  className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white px-5 py-2 rounded-lg font-medium transition"
                >
                  Mark Attendance
                </button>
              </div>
            )}
            {selectedApps.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-5 rounded-xl mb-6 flex flex-col md:flex-row items-center gap-4 shadow-inner border border-blue-200">
                <p className="font-semibold text-gray-700 text-lg">
                  Update Stage for{" "}
                  <span className="text-blue-600">{selectedApps.length}</span>{" "}
                  selected
                </p>
                <div className="flex gap-2 items-center">
                  <label className="text-gray-600 font-medium">Stage:</label>
                  <select
                    value={stage}
                    onChange={(e) => setStage(e.target.value)}
                    className="border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition"
                  >
                    <option value="induction">Induction</option>
                    <option value="training">Training</option>
                    <option value="ojt">OJT</option>
                    <option value="floor">Floor</option>
                  </select>
                </div>

                <button
                  onClick={handleMarkStage}
                  disabled={loading || !selectedApps?.length}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-medium transition"
                >
                  {loading ? "Marking..." : `Mark ${stage} Completed`}
                </button>
              </div>
            )}

            {selectedApps.length > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-5 rounded-xl mb-6 flex flex-col md:flex-row items-center gap-4 shadow-inner border border-green-200">
                <p className="font-semibold text-gray-700 text-lg">
                  Update Training Status for{" "}
                  <span className="text-green-600">{selectedApps.length}</span>{" "}
                  selected
                </p>

                {/* Training Status Dropdown */}
                <div className="flex gap-2 items-center">
                  <label className="text-gray-600 font-medium">
                    Training Status:
                  </label>
                  <select
                    value={trainingStatus}
                    onChange={(e) => setTrainingStatus(e.target.value)}
                    className="border border-yellow-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none transition"
                  >
                    <option value="">-- Select Status --</option>
                    <option value="not-started">Not Started</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="rejected">Rejected</option>
                    <option value="hold">Hold</option>
                    <option value="mockcall-certified">
                      Mock Call Certified
                    </option>
                    <option value="mockcall-not-certified">
                      Mock Call Not Certified
                    </option>
                    <option value="ojt-certified">OJT Certified</option>
                    <option value="ojt-not-certified">OJT Not Certified</option>
                    <option value="abscond">Abscond</option>
                    <option value="dropped">Dropped</option>
                    <option value="atl">ATL</option>
                  </select>
                </div>

                {/* Update Button */}
                <button
                  onClick={updateTrainingStatus}
                  disabled={loading || !selectedApps.length || !trainingStatus}
                  className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-medium transition"
                >
                  {loading ? "Updating..." : `Update to "${trainingStatus}"`}
                </button>
              </div>
            )}

            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
              <h2 className="text-2xl font-bold text-gray-800">Applications</h2>

              <div className="flex flex-wrap gap-3 items-center">
                <button
                  onClick={() => setShowAddAppModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-medium transition"
                >
                  + Add Application
                </button>
                {showAddAppModal && (
                  <AddApplicationModal
                    onClose={() => setShowAddAppModal(false)}
                    onAdd={(ids) => addApplicationToBatch(ids)}
                  />
                )}
                {/* {selectedApps.length > 0 &&
                  batch.batchStatus === "receivedByInduction" && ( */}
                <div className="flex gap-3">
                  <button
                    onClick={markBulkJoined}
                    className="px-4 py-2 bg-[#7F00FF] text-white rounded"
                  >
                    Mark Joined
                  </button>
                  <button
                    onClick={markBulkTrainingStarted}
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Start Training
                  </button>

                  <button
                    onClick={markBulkTrainingCompleted}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                  >
                    Complete Training
                  </button>

                  {/* <button
                        onClick={markBulkInductionCompleted}
                        className="px-4 py-2 bg-blue-600 text-white rounded"
                      >
                        Mark Induction Completed
                      </button> */}
                </div>
                {/* )} */}
                {/* </div> */}

                <button
                  onClick={markAbscondedApplications}
                  className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg font-medium transition"
                >
                  Mark Absconded
                </button>

                <button
                  onClick={() => markApplicationsBulkStatus("decertified")}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 rounded-lg font-medium transition"
                >
                  Mark Decertified
                </button>
                <button
                  onClick={() => markApplicationsBulkStatus("certified")}
                  className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-medium transition"
                >
                  Mark Certified
                </button>
              </div>
            </div>

            {/* Applications Table */}
            <div className="overflow-x-auto rounded-lg shadow border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3">
                      <input
                        type="checkbox"
                        checked={
                          selectedApps.length === batch.applications.length &&
                          batch.applications.length > 0
                        }
                        onChange={toggleSelectAll}
                        className="accent-blue-500"
                      />
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Application ID
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Application Status
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Candidate
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Email
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Joining Date
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Status
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Induction
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Training
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      OJT
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Floor
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Training Status
                    </th>
                    <th className="p-3 text-left font-medium text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {batch.applications.map((app) => (
                    <tr
                      key={app._id}
                      className="hover:bg-gray-50 transition cursor-pointer"
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedApps.includes(app.applicationObjId)}
                          onChange={() => toggleSelectApp(app.applicationObjId)}
                          className="accent-blue-500"
                        />
                      </td>

                      <td className="p-3 font-medium text-gray-800">
                        {app.applicationId}
                      </td>

                      <td className="p-3 font-medium text-gray-800">
                        <Badge
                          status={app.applicationDetails?.applicationStatus}
                        />
                      </td>
                      <td className="p-3 text-gray-700 font-semibold">
                        {app.applicationDetails?.candidateSnapshot?.firstName}{" "}
                        {app.applicationDetails?.candidateSnapshot?.lastName}
                      </td>
                      <td className="p-3 text-gray-600">
                        <p>
                          {app.applicationDetails?.candidateSnapshot?.email}
                        </p>
                        <p>
                          {app.applicationDetails?.candidateSnapshot?.phone}
                        </p>
                      </td>
                      <td className="p-3 text-gray-600">
                        {new Date(app?.joinDate).toLocaleDateString()}
                      </td>
                      <td className="p-3">
                        <Badge status={app.applicationStatus} />
                      </td>
                      <td className="p-3">
                        {app.stageProgress?.induction?.status}
                      </td>
                      <td className="p-3">
                        {app.stageProgress?.training?.status}
                      </td>
                      <td className="p-3">{app.stageProgress?.ojt?.status}</td>
                      <td className="p-3">
                        {app.stageProgress?.floor?.status}
                      </td>
                      <td className="p-3 text-gray-600">
                        {app.applicationDetails?.trainingDetails?.status || "-"}
                      </td>
                      {/* <td className="p-3">
                        {app.applicationDetails?.trainingDetails?.status ? (
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold 
        ${
          {
            "not-started": "bg-gray-100 text-gray-600",
            "in-progress": "bg-blue-100 text-blue-700",
            completed: "bg-green-100 text-green-700",
            rejected: "bg-red-100 text-red-700",
            hold: "bg-yellow-100 text-yellow-700",
            "mockcall-certified": "bg-emerald-100 text-emerald-700",
            "mockcall-not-certified": "bg-rose-100 text-rose-700",
            "ojt-certified": "bg-teal-100 text-teal-700",
            "ojt-not-certified": "bg-orange-100 text-orange-700",
            abscond: "bg-purple-100 text-purple-700",
            dropped: "bg-pink-100 text-pink-700",
            atl: "bg-indigo-100 text-indigo-700",
          }[app.applicationDetails.trainingDetails.status] ||
          "bg-gray-100 text-gray-600"
        }`}
                          >
                            {app.applicationDetails.trainingDetails.status}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td> */}

                      <td className="p-3 flex flex-wrap gap-2">
                        <button
                          onClick={() =>
                            handleRemoveClick(app.applicationObjId)
                          }
                          className="bg-red-500 text-white px-2 py-1 rounded text-xs"
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => {
                            setUrl(
                              `/nolayout/applications/${app.applicationObjId}/manage`
                            );
                            setShow(true);
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-xs transition"
                        >
                          Open Here
                        </button>
                        {show && (
                          <PopupWithUrl
                            url={url}
                            onClose={() => setShow(false)}
                            applicationObjId={app.applicationObjId}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white shadow-md rounded-2xl p-6 overflow-x-auto">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Training Attendance Sheet
            </h2>

            <table className="min-w-full border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left border">Application ID</th>
                  <th className="px-4 py-2 text-left border">Candidate Name</th>

                  {/* Dynamic date headers */}
                  {dates.map((date) => (
                    <th key={date} className="px-4 py-2 text-center border">
                      {dayjs(date).format("DD MMM")}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {batch.applications.map((app) => {
                  return (
                    <tr key={app._id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border">{app.applicationId}</td>
                      <td className="px-4 py-2 border">
                        {app.applicationDetails?.candidateSnapshot?.firstName}{" "}
                        {app.applicationDetails?.candidateSnapshot?.lastName}
                      </td>

                      {/* Attendance per date */}
                      {dates.map((date) => {
                        const record = app.attendance.find(
                          (a) =>
                            new Date(a.date).toISOString().split("T")[0] ===
                            date
                        );
                        return (
                          <td
                            key={date}
                            className="px-4 py-2 text-center border"
                          >
                            {record ? (
                              <span
                                className={`px-2 py-1 rounded text-xs ${
                                  record.status === "present"
                                    ? "bg-green-100 text-green-700"
                                    : record.status === "absent"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {record.status === "present"
                                  ? "P"
                                  : record.status === "absent"
                                  ? "A"
                                  : "W"}{" "}
                                {/* W = weekoff */}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* History (Timeline style) */}
          <div className="bg-white shadow-md rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-3 text-gray-800">
              History
            </h2>
            <div className="relative border-l-2 border-gray-200 pl-4 space-y-4">
              {batch.history.map((h, idx) => (
                <div key={idx} className="relative">
                  {/* <div className="absolute -left-3 top-1 w-6 h-6 bg-blue-500 rounded-full border-2 border-white"></div> */}
                  <div className="bg-gray-50 p-3 rounded-lg shadow-sm">
                    <p className="text-gray-700 font-medium">{h.status}</p>
                    <p className="text-sm text-gray-500">{h.notes}</p>
                    <p className="text-sm text-gray-500">{h.remarks}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      By {h.changedBy?.userName} on{" "}
                      {new Date(h.changedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {batch.applications.map((app) => (
              <div
                key={app._id}
                className="border rounded-xl p-5 shadow-sm hover:shadow-lg transition relative bg-white"
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  className="absolute top-3 left-3 w-4 h-4"
                  checked={selectedApps.includes(app.applicationObjId)}
                  onChange={() => toggleSelectApp(app.applicationObjId)}
                />

                {/* Candidate Name + ID */}
                <div className="mb-2">
                  <h3 className="text-lg font-bold text-gray-800">
                    {app.applicationDetails?.candidateSnapshot?.firstName}{" "}
                    {app.applicationDetails?.candidateSnapshot?.lastName}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Application ID: {app.applicationId}
                  </p>
                </div>

                {/* Contact Info */}
                <div className="flex items-center text-sm text-gray-600 gap-3 mb-3">
                  <span className="flex items-center gap-1">
                    📧 {app.applicationDetails?.candidateSnapshot?.email}
                  </span>
                  <span className="flex items-center gap-1">
                    📞 {app.applicationDetails?.candidateSnapshot?.phone}
                  </span>
                </div>

                {/* Status Progress */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Induction</span>
                    <span>Training</span>
                    <span>OJT</span>
                    <span>Floor</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1">
                    <span
                      className={`h-2 rounded ${
                        app.inductionStatus === "completed"
                          ? "bg-green-500"
                          : app.inductionStatus === "pending"
                          ? "bg-yellow-400"
                          : "bg-red-400"
                      }`}
                    />
                    <span
                      className={`h-2 rounded ${
                        app.trainingStatus === "completed"
                          ? "bg-green-500"
                          : app.trainingStatus === "pending"
                          ? "bg-yellow-400"
                          : "bg-red-400"
                      }`}
                    />
                    <span
                      className={`h-2 rounded ${
                        app.ojtStatus === "completed"
                          ? "bg-green-500"
                          : app.ojtStatus === "pending"
                          ? "bg-yellow-400"
                          : "bg-red-400"
                      }`}
                    />
                    <span
                      className={`h-2 rounded ${
                        app.floorStatus === "completed"
                          ? "bg-green-500"
                          : app.floorStatus === "pending"
                          ? "bg-yellow-400"
                          : "bg-red-400"
                      }`}
                    />
                  </div>
                </div>

                {/* Attendance snapshot (last 5) */}
                <div className="flex items-center gap-1 mb-4">
                  {app.attendance.slice(-5).map((att) => (
                    <span
                      key={att._id}
                      className={`w-3 h-3 rounded-full ${
                        att.status === "present"
                          ? "bg-green-500"
                          : att.status === "absent"
                          ? "bg-red-500"
                          : "bg-gray-300"
                      }`}
                      title={`${new Date(att.date).toLocaleDateString()} - ${
                        att.status
                      }`}
                    ></span>
                  ))}
                </div>

                {/* Action */}
                <div className="flex justify-end">
                  <Link
                    to={`/applications/${app.applicationObjId}/manage`}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg"
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {viewApp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-lg p-6 relative">
              {/* ❌ Close Button */}
              <button
                onClick={handleCloseApplication}
                className="absolute top-3 right-3 text-gray-500 hover:text-red-600 text-xl"
              >
                ✖
              </button>

              {/* Candidate Header */}
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                {viewApp.applicationDetails?.candidateSnapshot?.firstName}{" "}
                {viewApp.applicationDetails?.candidateSnapshot?.lastName}
              </h2>

              {/* Candidate Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Application ID</p>
                  <p className="font-medium">{viewApp.applicationId}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">
                    {viewApp.applicationDetails?.candidateSnapshot?.email}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">
                    {viewApp.applicationDetails?.candidateSnapshot?.phone}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Joining Date</p>
                  <p className="font-medium">
                    {new Date(viewApp?.joinDate).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="mt-4">
                <p className="text-gray-500">Status</p>
                <Badge status={viewApp.applicationStatus} />
              </div>
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default ManageBatch;

const PopupWithUrl = ({ url, onClose, applicationObjId }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50">
      <div className="bg-white rounded-2xl w-[90%] h-[90%] shadow-xl relative">
        <div className="p-3 bg-gray-100 rounded-t-2xl text-center font-semibold text-gray-800">
          <Link
            className="absolute left-3 top-3 text-blue-600 underline"
            to={`/applications/${applicationObjId}/manage`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Go to Full Page
          </Link>
          Document Preview
          <button
            onClick={onClose}
            className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full"
          >
            ✕
          </button>
        </div>
        <iframe
          src={url}
          title="Dynamic Page"
          className="w-full h-full rounded-2xl"
        />
      </div>
    </div>
  );
};

const AddApplicationModal = ({ onClose, onAdd }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);

  const handleSearch = () => {
    let query = {};

    // Search by name/email/applicationId
    if (search) {
      query.$or = [
        { "candidateSnapshot.firstName": { $regex: search, $options: "i" } },
        { "candidateSnapshot.lastName": { $regex: search, $options: "i" } },
        { "candidateSnapshot.email": { $regex: search, $options: "i" } },
        { "candidateSnapshot.phone": { $regex: search, $options: "i" } },
        { id: { $regex: search, $options: "i" } },
      ];
    }

    fetchApplications(query);
  };
  const fetchApplications = async (queryObj = {}) => {
    const token = getCookie("swtoken");
    try {
      const res = await axios.post(
        `${apiurl}/api/application/search`,
        queryObj,
        {
          headers: { Authorization: `${token}` },
        }
      );
      setResults(res.data.data || []);
      //   setFilteredApplications(res.data.data || []);
    } catch (err) {
      console.error("Error fetching applications:", err);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAdd = () => {
    if (selected.length > 0) {
      onAdd(selected);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50">
      <div className="bg-white rounded-2xl w-[90%] max-w-3xl p-6 shadow-lg relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-4 text-gray-800">
          Add Applications
        </h2>

        {/* Search bar */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Search by name, email, or ID..."
            className="flex-1 border px-3 py-2 rounded-lg"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            onClick={handleSearch}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            Search
          </button>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2">Select</th>
                <th className="p-2">Application ID</th>
                <th className="p-2">Candidate</th>
                <th className="p-2">Email</th>
              </tr>
            </thead>
            <tbody>
              {results.map((app) => (
                <tr key={app._id} className="border-b">
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={selected.includes(app._id)}
                      onChange={() => toggleSelect(app._id)}
                    />
                  </td>
                  <td className="p-2">{app.id}</td>
                  <td className="p-2">
                    {app.candidateSnapshot?.firstName}{" "}
                    {app.candidateSnapshot?.lastName}
                  </td>
                  <td className="p-2">{app.candidateSnapshot?.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border">
            Cancel
          </button>
          <button
            onClick={handleAdd}
            className="px-4 py-2 rounded-lg bg-green-600 text-white"
          >
            Add Selected
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusUpdateModal = ({
  isOpen,
  onClose,
  onUpdate,
  currentStatus,
  options,
}) => {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  useEffect(() => {
    if (options.length > 0) {
      setSelectedStatus(options[0]);
    }
  }, [options]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">
          Move from <span className="capitalize">{currentStatus}</span>
        </h2>

        {/* Status options */}
        <div className="space-y-2">
          {options.map((st) => (
            <label
              key={st}
              className="flex items-center space-x-2 cursor-pointer"
            >
              <input
                type="radio"
                name="status"
                value={st}
                checked={selectedStatus === st}
                onChange={() => setSelectedStatus(st)}
              />
              <span className="capitalize">{st}</span>
            </label>
          ))}
        </div>

        {/* Remarks */}
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter remarks..."
          className="w-full border rounded-lg p-2 text-sm"
          rows="3"
        />

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700"
          >
            Close
          </button>
          <button
            disabled={!selectedStatus}
            onClick={() => onUpdate(selectedStatus, remarks)}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
};

// const backdrop = {
//   visible: { opacity: 1 },
//   hidden: { opacity: 0 },
// };

// const modal = {
//   hidden: { y: "-100vh", opacity: 0 },
//   visible: { y: "0", opacity: 1, transition: { delay: 0.1 } },
// };

// const SendBatchReportModal = ({ show, onClose, onSend, startDate, endDate, reportData }) => {
//   const [loading, setLoading] = useState(true);

//   console.log("reportData in modal:", reportData);

//     const [error, setError] = useState("");
//     const [uploading, setUploading] = useState(false);
//     const [uploadMessage, setUploadMessage] = useState("");

//     const [emailData, setEmailData] = useState({
//       ccEmails: [],
//       subject: "",
//       message: "",
//       toEmails: [],
//       attachment:""
//     });

//   const handleFileUpload = async (e, field) => {
//     console.log("Uploading field:", field);
//     const file = e.target.files[0];
//     if (!file) return;

//     setUploading(true);
//     setUploadMessage("");

//     const formData = new FormData();
//     formData.append("file", file);

//     try {
//       const response = await axios.post(
//         `${apiurl}/api/media/interview-tracker`,
//         formData,
//         {
//           headers: { "Content-Type": "multipart/form-data" },
//         }
//       );

//       if (response?.data?.success) {
//         console.log("Upload response:", response);
//         // setFormData((prev) => ({
//         //   ...prev,
//         //   [field]: response.data.fileUrl, // Ensure correct field is updated
//         // }));

//         setEmailData((prev) => ({
//           ...prev,
//           attachment: response.data.fileUrl,
//         }));

//         setUploadMessage("File uploaded successfully!");
//       } else {
//         setUploadMessage("File upload failed. Please try again.");
//       }
//     } catch (error) {
//       console.error("Upload error:", error);
//       setUploadMessage("Error uploading file.");
//     } finally {
//       setUploading(false);
//     }
//   };

//   const sendEmailReport = async () => {
//     try {
//       setLoading(true);
//       const response = await axios.post(
//         `${apiurl}/api/analytic/send-batch-handover-report`,
//         {
//           toEmails: emailData.toEmails,
//           ccEmails: emailData.ccEmails,
//           subject: emailData.subject,
//           message: emailData.message,
//           attachment: emailData.attachment,
//           data: {
//             agents: reportData,
//           },
//         }
//       );
//       alert("✅ Report sent successfully!");
//       onClose(); // Close modal after sending
//     } catch (err) {
//       console.error("Failed to send report:", err);
//       alert("❌ Failed to send report.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   console.log("emailData:", emailData);

//   useEffect(() => {
//     const handleEscape = (e) => {
//       if (e.key === "Escape") onClose();
//     };
//     document.addEventListener("keydown", handleEscape);
//     return () => document.removeEventListener("keydown", handleEscape);
//   }, [onClose]);

//   return (
//     <AnimatePresence>
//       {show && (
//         <motion.div
//           className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50 flex items-center justify-center"
//           variants={backdrop}
//           initial="hidden"
//           animate="visible"
//           exit="hidden"
//           onClick={onClose}
//         >
//           <motion.div
//             className="bg-white rounded-2xl p-6 w-full max-w-4xl shadow-xl relative overflow-y-auto max-h-[90vh]"
//             variants={modal}
//             initial="hidden"
//             animate="visible"
//             exit="hidden"
//             onClick={(e) => e.stopPropagation()}
//           >
//             <h3 className="text-2xl font-bold text-indigo-700 mb-4">
//               📧 Send Batch Report
//             </h3>
//             <form
//               onSubmit={(e) => {
//                 e.preventDefault();
//               }}
//               className="space-y-4 mt-6 border-t pt-6"
//             >
//               <div className="flex flex-col w-full justify-between items-center">
//                 <EmailInputField
//                   label="To Email *"
//                   value={emailData.toEmails}
//                   onChange={(newList) =>
//                     setEmailData((prev) => ({ ...prev, toEmails: newList }))
//                   }
//                 />

//                 <EmailInputField
//                   label="CC Email"
//                   value={emailData.ccEmails}
//                   onChange={(newList) =>
//                     setEmailData((prev) => ({ ...prev, ccEmails: newList }))
//                   }
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700">
//                   Subject
//                 </label>
//                 <input
//                   type="text"
//                   value={emailData.subject}
//                   onChange={(e) =>
//                     setEmailData((prev) => ({
//                       ...prev,
//                       subject: e.target.value,
//                     }))
//                   }
//                   className="w-full border border-gray-300 px-4 py-2 rounded-md"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700">
//                   Message
//                 </label>
//                 <textarea
//                   value={emailData.message}
//                   onChange={(e) =>
//                     setEmailData((prev) => ({
//                       ...prev,
//                       message: e.target.value,
//                     }))
//                   }
//                   rows={3}
//                   className="w-full border border-gray-300 px-4 py-2 rounded-md"
//                 />
//               </div>

//               <h3 className="text-md font-semibold mt-8 mb-4">
//                 Upload Attachment
//               </h3>
//               <input
//                 type="file"
//                 accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
//                 onChange={(e) => handleFileUpload(e, "attachment")}
//                 className="w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
//               />

//               {uploading && <p className="text-blue-500">Uploading...</p>}
//               {uploadMessage && (
//                 <p className="text-green-500">{uploadMessage}</p>
//               )}

//               <div className="flex justify-end">
//                 <button
//                   className="w-full bg-indigo-600 text-white py-2 mt-4 rounded-md"
//                   onClick={sendEmailReport}
//                 >
//                   {loading ? "Sending..." : "Send Report"}
//                 </button>
//               </div>
//             </form>
//           </motion.div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// };

const backdrop = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
};

const modal = {
  hidden: { y: "-100vh", opacity: 0 },
  visible: { y: "0", opacity: 1, transition: { delay: 0.1 } },
};

const SendBatchReportModal = ({ show, onClose, reportData }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");

  // ❗ FIXED: should NOT start with loading=true
  const [loading, setLoading] = useState(false);

  // ❗ FIXED: stable default values
  const [emailData, setEmailData] = useState({
    toEmails: [],
    ccEmails: [],
    subject: "Batch Handover Report",
    message: "",
    attachment: "",
  });

  // ===========================
  // 📤 FILE UPLOAD
  // ===========================
  // const handleFileUpload = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   setUploading(true);
  //   setUploadMessage("");

  //   const formData = new FormData();
  //   formData.append("file", file);

  //   try {
  //     const response = await axios.post(
  //       `${apiurl}/api/media/interview-tracker`,
  //       formData,
  //       { headers: { "Content-Type": "multipart/form-data" } }
  //     );

  //     if (response?.data?.success) {
  //       setEmailData((prev) => ({
  //         ...prev,
  //         attachment: response.data.fileUrl,
  //       }));
  //       setUploadMessage("File uploaded successfully!");
  //     } else {
  //       setUploadMessage("File upload failed. Try again.");
  //     }
  //   } catch (error) {
  //     console.error("Upload error:", error);
  //     setUploadMessage("Error uploading file.");
  //   } finally {
  //     setUploading(false);
  //   }
  // };

  // ===========================
  // 📧 SEND EMAIL
  // ===========================
  const sendEmailReport = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${apiurl}/api/analytic/send-batch-handover-report`,
        {
          toEmails: emailData.toEmails,
          ccEmails: emailData.ccEmails,
          subject: emailData.subject,
          message: emailData.message,
          attachment: emailData.attachment,
          data: {
            agents: reportData,
          },
        }
      );

      alert("✅ Report sent successfully!");
      onClose();
    } catch (err) {
      console.error("Failed to send report:", err);
      alert("❌ Failed to send report.");
    } finally {
      setLoading(false);
    }
  };

  // Close on ESC Key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50 flex items-center justify-center"
          variants={backdrop}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-2xl p-6 w-full max-w-5xl shadow-xl relative overflow-y-auto max-h-[90vh]"
            variants={modal}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-indigo-700 mb-4">
              📧 Send Batch Handover Report
            </h3>

            {/* ==============================
                EMAIL FORM
            =============================== */}
            <form className="space-y-4 border-t pt-6">
              {/* To Email */}
              <EmailInputField
                label="To Email *"
                value={emailData.toEmails}
                onChange={(newList) =>
                  setEmailData((prev) => ({ ...prev, toEmails: newList }))
                }
              />

              {/* CC Email */}
              <EmailInputField
                label="CC Email"
                value={emailData.ccEmails}
                onChange={(newList) =>
                  setEmailData((prev) => ({ ...prev, ccEmails: newList }))
                }
              />

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) =>
                    setEmailData((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  className="w-full border px-4 py-2 rounded-md"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  value={emailData.message}
                  onChange={(e) =>
                    setEmailData((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full border px-4 py-2 rounded-md"
                />
              </div>

              {/* File Upload */}
              {/* <h3 className="text-md font-semibold mt-8 mb-2">
                Upload Attachment (Optional)
              </h3> */}

              {/* <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                onChange={handleFileUpload}
                className="w-full p-3 border rounded-xl"
              /> */}

              {uploading && <p className="text-blue-500">Uploading...</p>}
              {uploadMessage && (
                <p className="text-green-600">{uploadMessage}</p>
              )}
            </form>

            {/* ==============================
                TABLE OF APPLICATIONS
            =============================== */}
            <h3 className="text-lg font-semibold mt-10 mb-3">
              Batch Applications
            </h3>

            <div className="overflow-x-auto border rounded-lg mb-6">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 border">S.No</th>
                    <th className="p-2 border">Emp ID</th>
                    <th className="p-2 border">Name</th>
                    {/* <th className="p-2 border">Email</th>
                    <th className="p-2 border">Phone</th> */}
                    <th className="p-2 border">Reference</th>
                    <th className="p-2 border">D.O.J</th>
                    {/* <th className="p-2 border">Designation</th> */}
                    <th className="p-2 border">Total Experience</th>
                    <th className="p-2 border">Experience in Role</th>
                    <th className="p-2 border">Last Organization</th>
                    <th className="p-2 border">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.map((a, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="p-2 border">{i + 1}</td>
                      <td className="p-2 border">{a.applicationId}</td>
                      <td className="p-2 border">
                        {a.applicationDetails.candidateSnapshot?.firstName}{" "}
                        {a.applicationDetails.candidateSnapshot?.lastName}
                      </td>
                      {/* <td className="p-2 border">{a.applicationDetails.candidateSnapshot?.email}</td>
                      <td className="p-2 border">{a.applicationDetails.candidateSnapshot?.phone}</td> */}
                      <td className="p-2 border capitalize">
                        {(() => {
                          const ref = a.applicationDetails.referenceDetails;

                          if (!ref) return "N/A";

                          const type = ref.referenceType;

                          switch (type) {
                            case "walkin":
                              return `Walk-in${
                                ref.walkInReference
                                  ? ` - ${ref.walkInReference}`
                                  : ""
                              }`;

                            case "recruiter":
                              return `Recruiter - ${
                                ref.recruiterName || "N/A"
                              }`;

                            case "employee":
                              return `Employee - ${
                                ref.employeeName || "N/A"
                              } (${ref.employeeId || "N/A"})`;

                            case "consultancy":
                              return `Consultancy - ${
                                ref.consultancyReference || "N/A"
                              }`;

                            case "other":
                              return `Other - ${ref.otherReference || "N/A"}`;

                            default:
                              return "N/A";
                          }
                        })()}
                      </td>

                      <td className="p-2 border capitalize">
                        {a.applicationDetails.offerDetails?.joiningDate}
                      </td>
                      {/* <td className="p-2 border capitalize">{a.applicationDetails.candidateSnapshot?.designation}</td> */}
                      <td className="p-2 border capitalize">
                        {a.applicationDetails.professionalInfo?.totalExperience
                          ? `${a.applicationDetails.professionalInfo.totalExperience} yrs`
                          : a.applicationDetails.professionalInfo?.experienceType}
                      </td>

                      <td className="p-2 border capitalize">
                        {a.applicationDetails.professionalInfo
                          ?.process
                          ? `${a.applicationDetails.professionalInfo.process}`
                          : "N/A"}
                      </td>

                      <td className="p-2 border capitalize">
                        {a.applicationDetails.professionalInfo
                          ?.previousCompany || "N/A"}
                      </td>

                      <td className="p-2 border capitalize">
                        {a.applicationStatus}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* SEND BUTTON */}
            <div className="flex justify-end">
              <button
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg shadow-md"
                onClick={sendEmailReport}
              >
                {loading ? "Sending..." : "Send Report"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
